import i18n from "../util/i18n.ts";
import { PositionText } from "../util/positionNames.tsx";

type Props = {
  className?: string;
  players: {
    ratings: {
      pos: string;
    };
  }[];
};

const TARGET_DEPTH_BY_POSITION: Record<string, number> = {
  C: 2,
  PF: 2,
  PG: 2,
  SF: 2,
  SG: 2,
};

export const getRosterPositionNeeds = (players: Props["players"]) => {
  const counts = new Map<string, number>();
  for (const player of players) {
    counts.set(player.ratings.pos, (counts.get(player.ratings.pos) ?? 0) + 1);
  }

  return Object.entries(TARGET_DEPTH_BY_POSITION)
    .map(([pos, target]) => ({
      count: counts.get(pos) ?? 0,
      pos,
      target,
    }))
    .filter(({ count, target }) => count < target)
    .sort((a, b) => a.count - b.count || a.pos.localeCompare(b.pos));
};

export const RosterComposition = ({ className, players }: Props) => {
  const needs = getRosterPositionNeeds(players);
  const counts = new Map<string, number>();
  for (const player of players) {
    counts.set(player.ratings.pos, (counts.get(player.ratings.pos) ?? 0) + 1);
  }
  const positions = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  return (
    <section className={className}>
      <div className="card">
        <div className="card-body py-2">
          <div className="small text-body-secondary">
            {i18n.t("rosterComposition.title", {
              ns: "ui",
              defaultValue: "Roster needs",
            })}
          </div>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {needs.length > 0 ? (
              needs.slice(0, 3).map(({ pos }) => (
                <span className="badge text-bg-warning" key={pos}>
                  {i18n.t("rosterComposition.need", {
                    ns: "ui",
                    defaultValue: "Need",
                  })}{" "}
                  <PositionText pos={pos} />
                </span>
              ))
            ) : (
              <span className="badge text-bg-success">
                {i18n.t("rosterComposition.balanced", {
                  ns: "ui",
                  defaultValue: "Balanced",
                })}
              </span>
            )}
            {positions.slice(0, 5).map(([pos, count]) => (
              <span className="badge text-bg-light border text-body" key={pos}>
                <PositionText pos={pos} /> {count}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
